import React, { useCallback, useEffect, useMemo, useState, type JSX } from 'react'
import {
  AlignLeft,
  Bot,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FileText,
  Loader2,
  Network,
  Plus,
  Search,
  Shield,
  Clock,
  Users,
  Pencil,
  Check,
  X,
} from 'lucide-react'
import RequirePermission from '../../components/auth/RequirePermission.js'
import { api } from '../../api/client.js'
import { useToast } from '../../components/system/ToastProvider.js'
import PromptFormDrawer, { type PromptFormData } from '../../components/admin/ai/PromptFormDrawer.js'
import PromptDetailDrawer from '../../components/admin/ai/PromptDetailDrawer.js'
import PromptAgentPickerDrawer, { type PickerAgentItem } from '../../components/admin/ai/PromptAgentPickerDrawer.js'
import { useConfirmDialog } from '../../components/ui/ConfirmDialog.js'
import Select from '../../components/ui/Select.js'

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface PromptLinkedAgent {
  id: string
  slug: string
  name: string
  type: string
}

interface PromptVersion {
  version: number
  content: string
  variables: string[]
  updatedBy: string | null
  createdAt: string
  notes?: string
}

interface PromptTemplate {
  id: string
  slug: string
  name: string
  description: string
  category: 'orchestrator' | 'agent' | 'routing' | 'summary' | 'safety'
  currentVersion: number
  content: string
  variables: string[]
  updatedAt: string
  versions: PromptVersion[]
  linkedAgents: PromptLinkedAgent[]
}

interface AgentListItem {
  id: string
  slug: string
  name: string
  description: string | null
  type: string
  // GET /admin/ai/agents returns snake_case columns straight from the DB.
  is_system: boolean
  is_enabled: boolean
}

// -----------------------------------------------------------------------------
// Theme
// -----------------------------------------------------------------------------

const CATEGORY_META: Record<
  PromptTemplate['category'],
  { label: string; icon: JSX.Element; badge: string; bg: string; text: string }
> = {
  agent: {
    label: 'Agent',
    icon: <Bot className="h-3.5 w-3.5" />,
    badge: 'bg-blue-100 text-blue-800 border-blue-200',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
  },
  orchestrator: {
    label: 'Orchestrator',
    icon: <Network className="h-3.5 w-3.5" />,
    badge: 'bg-orange-100 text-orange-800 border-orange-200',
    bg: 'bg-orange-50',
    text: 'text-orange-700',
  },
  routing: {
    label: 'Routing',
    icon: <Network className="h-3.5 w-3.5" />,
    badge: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
  },
  summary: {
    label: 'Summary',
    icon: <AlignLeft className="h-3.5 w-3.5" />,
    badge: 'bg-purple-100 text-purple-800 border-purple-200',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
  },
  safety: {
    label: 'Safety',
    icon: <Shield className="h-3.5 w-3.5" />,
    badge: 'bg-red-100 text-red-800 border-red-200',
    bg: 'bg-red-50',
    text: 'text-red-700',
  },
}

const CATEGORIES: ('all' | PromptTemplate['category'])[] = [
  'all',
  'agent',
  'orchestrator',
  'routing',
  'summary',
  'safety',
]

const CATEGORY_OPTIONS = CATEGORIES.slice(1) as PromptTemplate['category'][]

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function mapPromptToFormData(p: PromptTemplate): PromptFormData {
  return {
    id: p.id,
    name: p.name,
    slug: p.slug,
    description: p.description,
    category: p.category,
    content: p.content,
    notes: '',
    currentVersion: p.currentVersion,
    updatedAt: p.updatedAt,
    variables: p.variables,
  }
}

/**
 * Builds `agentId → prompt name` for every prompt EXCEPT `excludePromptId`.
 * Because `ai_agents.prompt_id` holds a single prompt, any agent appearing in
 * this map would have that prompt replaced (and its system prompt text
 * overwritten) if it were assigned to the excluded prompt instead.
 */
function buildOtherPromptByAgentId(
  prompts: PromptTemplate[],
  excludePromptId: string | undefined,
): Record<string, string> {
  const map: Record<string, string> = {}
  for (const p of prompts) {
    if (p.id === excludePromptId) continue
    for (const a of p.linkedAgents ?? []) {
      map[a.id] = p.name
    }
  }
  return map
}

function mapAgentToPickerItem(a: AgentListItem): PickerAgentItem {
  return {
    id: a.id,
    slug: a.slug,
    name: a.name,
    type: a.type,
    description: a.description ?? undefined,
    isEnabled: a.is_enabled,
  }
}

/** Format an ISO timestamp as a short relative date string (matches Tool Catalog). */
function formatTimestamp(iso: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHr / 24)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHr < 24) return `${diffHr}h ago`
  if (diffDay < 30) return `${diffDay}d ago`
  return d.toLocaleDateString()
}

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------

export default function AiPromptsPage(): JSX.Element {
  const toast = useToast()
  const { confirm, Dialog } = useConfirmDialog()
  const [prompts, setPrompts] = useState<PromptTemplate[]>([])
  const [agents, setAgents] = useState<AgentListItem[]>([])
  const [selectedPrompt, setSelectedPrompt] = useState<PromptTemplate | null>(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<'all' | PromptTemplate['category']>('all')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [drawerInitial, setDrawerInitial] = useState<PromptFormData | null>(null)
  const [attachedGuardrailIds, setAttachedGuardrailIds] = useState<string[]>([])
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [isAgentPickerOpen, setIsAgentPickerOpen] = useState(false)
  const [agentPickerPrompt, setAgentPickerPrompt] = useState<PromptTemplate | null>(null)
  const [_isAssignSaving, setIsAssignSaving] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [sortBy, setSortBy] = useState<'updated' | 'name' | 'version'>('updated')
  const [expandedPrompt, setExpandedPrompt] = useState<string | null>(null)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const matched = prompts.filter((p) => {
      const matchesSearch =
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.slug.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.content.toLowerCase().includes(q)
      const matchesCategory = categoryFilter === 'all' ? true : p.category === categoryFilter
      return matchesSearch && matchesCategory
    })
    return [...matched].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name)
        case 'version':
          return b.currentVersion - a.currentVersion
        case 'updated':
        default:
          return b.updatedAt.localeCompare(a.updatedAt)
      }
    })
  }, [prompts, search, categoryFilter, sortBy])

  const totalPages = useMemo(() => Math.max(1, Math.ceil(filtered.length / limit)), [filtered.length, limit])
  const paginated = useMemo(() => {
    const start = (page - 1) * limit
    const end = start + limit
    return filtered.slice(start, end)
  }, [filtered, page, limit])
  const currentPage = Math.min(page, totalPages)
  const startIdx = filtered.length === 0 ? 0 : (currentPage - 1) * limit + 1
  const endIdx = Math.min(currentPage * limit, filtered.length)

  useEffect(() => {
    setPage(1)
  }, [search, categoryFilter, limit, sortBy])

  const counts = useMemo(() => {
    const c = { all: prompts.length } as Record<'all' | PromptTemplate['category'], number>
    for (const cat of CATEGORY_OPTIONS) {
      c[cat] = prompts.filter((p) => p.category === cat).length
    }
    return c
  }, [prompts])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [promptRes, agentRes] = await Promise.all([
        api.get<{ prompts: PromptTemplate[] }>('/admin/ai/prompts'),
        api.get<{ agents: AgentListItem[] }>('/admin/ai/agents'),
      ])
      setPrompts(promptRes.prompts ?? [])
      setAgents(agentRes.agents ?? [])
    } catch (err) {
      toast.error('Failed to load prompt templates', err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => {
    void load()
  }, [])

  function handleNewPrompt(): void {
    setDrawerInitial(null)
    setIsDrawerOpen(true)
  }

  async function handleEditPrompt(): Promise<void> {
    if (!selectedPrompt) return
    setIsDetailOpen(false)
    setDrawerInitial(mapPromptToFormData(selectedPrompt))
    // Load guardrails attached to this prompt
    try {
      const data = await api.get<{ guardrails: Array<{ guardrailId: string }> }>(`/admin/ai/prompts/${selectedPrompt.id}/guardrails`)
      setAttachedGuardrailIds(data.guardrails.map((g) => g.guardrailId))
    } catch {
      setAttachedGuardrailIds([])
    }
    setIsDrawerOpen(true)
  }

  function handleManageAgents(prompt: PromptTemplate): void {
    setAgentPickerPrompt(prompt)
    setIsAgentPickerOpen(true)
  }

  function toggleSelect(id: string): void {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAllOnPage(): void {
    const pageIds = paginated.map((p) => p.id)
    const allSelected = pageIds.every((id) => selectedIds.has(id))
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allSelected) {
        for (const id of pageIds) next.delete(id)
      } else {
        for (const id of pageIds) next.add(id)
      }
      return next
    })
  }

  function clearSelection(): void {
    setSelectedIds(new Set())
  }

  async function handleBulkAssign(): Promise<void> {
    const selectedPrompts = prompts.filter((p) => selectedIds.has(p.id))
    if (selectedPrompts.length === 0) return
    // Use the first selected prompt as the target for now
    // In a more advanced UI, we could show a drawer for bulk assignment
    setAgentPickerPrompt(selectedPrompts[0]!)
    setIsAgentPickerOpen(true)
  }

  async function handleSaveAgentAssignment(agentIds: string[]): Promise<void> {
    if (!agentPickerPrompt) return
    setIsAssignSaving(true)
    try {
      const res = await api.put<PromptTemplate>(`/admin/ai/prompts/${agentPickerPrompt.id}/agents`, {
        agentIds,
      })
      toast.success(
        'Agents assigned',
        `${agentIds.length} agent${agentIds.length === 1 ? '' : 's'} linked to "${agentPickerPrompt.name}"`,
      )
      setPrompts((prev) => prev.map((p) => (p.id === res.id ? res : p)))
      if (selectedPrompt?.id === res.id) setSelectedPrompt(res)
      setIsAgentPickerOpen(false)
      setAgentPickerPrompt(null)
    } catch (err) {
      toast.error('Failed to assign agents', err instanceof Error ? err.message : String(err))
    } finally {
      setIsAssignSaving(false)
    }
  }

  async function handleSave(data: PromptFormData, selectedAgentIds: string[], selectedGuardrailIds: string[]): Promise<void> {
    if (data.id) {
      try {
        const res = await api.put<PromptTemplate>(`/admin/ai/prompts/${data.id}`, {
          name: data.name.trim(),
          description: data.description.trim(),
          content: data.content,
          notes: data.notes.trim() || undefined,
        })
        // Assign agents if selection changed
        if (selectedAgentIds.length > 0) {
          await api.put<PromptTemplate>(`/admin/ai/prompts/${res.id}/agents`, {
            agentIds: selectedAgentIds,
          })
        }
        // Save guardrails attached to this prompt
        await api.put(`/admin/ai/prompts/${res.id}/guardrails`, {
          guardrails: selectedGuardrailIds.map((guardrailId, index) => ({ guardrailId, sortOrder: index })),
        })
        toast.success('Prompt saved', `Published as version v${res.currentVersion}${selectedAgentIds.length > 0 ? ` · assigned to ${selectedAgentIds.length} agent${selectedAgentIds.length === 1 ? '' : 's'}` : ''}`)
        setSelectedPrompt(res)
        await load()
        setIsDrawerOpen(false)
        setIsDetailOpen(true)
      } catch (err) {
        toast.error('Failed to save prompt', err instanceof Error ? err.message : String(err))
        throw err
      }
    } else {
      try {
        const res = await api.post<PromptTemplate>('/admin/ai/prompts', {
          slug: data.slug.trim(),
          name: data.name.trim(),
          description: data.description.trim(),
          category: data.category,
          content: data.content,
          notes: 'Initial creation',
        })
        // Assign agents after creation
        if (selectedAgentIds.length > 0) {
          const assigned = await api.put<PromptTemplate>(`/admin/ai/prompts/${res.id}/agents`, {
            agentIds: selectedAgentIds,
          })
          toast.success('Prompt created', `Registered ${res.name} · assigned to ${selectedAgentIds.length} agent${selectedAgentIds.length === 1 ? '' : 's'}`)
          setSelectedPrompt(assigned)
        } else {
          toast.success('Prompt created', `Registered ${res.name}`)
          setSelectedPrompt(res)
        }
        // Save guardrails after creation
        if (selectedGuardrailIds.length > 0) {
          await api.put(`/admin/ai/prompts/${res.id}/guardrails`, {
            guardrails: selectedGuardrailIds.map((guardrailId, index) => ({ guardrailId, sortOrder: index })),
          })
        }
        await load()
        setIsDrawerOpen(false)
        setIsDetailOpen(true)
      } catch (err) {
        toast.error('Failed to create prompt', err instanceof Error ? err.message : String(err))
        throw err
      }
    }
  }

  async function handleRollback(version: number): Promise<void> {
    if (!selectedPrompt) return
    confirm({
      title: 'Rollback Prompt',
      message: `Rollback prompt to version v${version}? This will create a new version with the old content.`,
      confirmLabel: 'Rollback',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        try {
          const res = await api.post<PromptTemplate>(`/admin/ai/prompts/${selectedPrompt.id}/rollback`, { version })
          toast.success('Prompt rolled back', `Created new version v${res.currentVersion} from v${version}`)
          setSelectedPrompt(res)
          await load()
        } catch (err) {
          toast.error('Rollback failed', err instanceof Error ? err.message : String(err))
        }
      },
    })
  }

  const pickerAgents = useMemo(() => agents.map(mapAgentToPickerItem), [agents])
  const assignedAgentIds = useMemo(() => {
    if (!agentPickerPrompt) return []
    return agentPickerPrompt.linkedAgents?.map((a) => a.id) ?? []
  }, [agentPickerPrompt])

  /**
   * agentId → name of a different prompt the agent is already linked to.
   * An agent holds a single `prompt_id`, so assigning it to another prompt
   * silently replaces that link — this drives the override warning.
   */
  const otherPromptByAgentId = useMemo(
    () => buildOtherPromptByAgentId(prompts, agentPickerPrompt?.id),
    [prompts, agentPickerPrompt],
  )
  const otherPromptForFormDrawer = useMemo(
    () => buildOtherPromptByAgentId(prompts, drawerInitial?.id),
    [prompts, drawerInitial],
  )

  const pageIds = paginated.map((p) => p.id)
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id))
  const someOnPageSelected = pageIds.some((id) => selectedIds.has(id)) && !allOnPageSelected

  // Full-page empty state
  if (!loading && prompts.length === 0) {
    return (
      <RequirePermission permission="admin:extensions:view">
        <div className="space-y-4">
          {/* Header bar */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Prompt Catalog</h3>
              <p className="text-xs text-gray-500">
                0 prompts. Create prompt templates and assign them to AI agents.
              </p>
            </div>
            <RequirePermission permission="admin:extensions:manage" fallback={null}>
              <button
                onClick={handleNewPrompt}
                className="flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#2a44a8]"
              >
                <Plus className="h-3.5 w-3.5" />
                Create Prompt
              </button>
            </RequirePermission>
          </div>

          {/* Empty state */}
          <div className="rounded-sm border border-gray-200 bg-white py-12 text-center shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#3454cf]/10">
              <FileText className="h-6 w-6 text-[#3454cf]" />
            </div>
            <h4 className="text-sm font-semibold text-gray-900">No prompt templates yet</h4>
            <p className="mx-auto mt-1 max-w-sm text-xs text-gray-500">
              Prompt templates let you define reusable system prompts and assign them to one or more AI agents. Use AI generation to bootstrap a prompt from a brief.
            </p>
            <div className="mx-auto mt-4 max-w-md rounded-sm border border-gray-100 bg-gray-50/50 p-3 text-left">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Quick start:</p>
              <ol className="space-y-1.5 text-[11px] text-gray-600">
                <li className="flex items-start gap-2">
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#3454cf] text-[9px] font-bold text-white">1</span>
                  Click <span className="font-medium text-[#3454cf]">"Create Prompt"</span> above
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#3454cf] text-[9px] font-bold text-white">2</span>
                  Use <span className="font-medium text-[#3454cf]">"Generate with AI"</span> or write your own
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#3454cf] text-[9px] font-bold text-white">3</span>
                  Save and assign it to one or more agents
                </li>
              </ol>
            </div>
            <RequirePermission permission="admin:extensions:manage" fallback={null}>
              <button
                onClick={handleNewPrompt}
                className="mx-auto mt-4 flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-medium text-white hover:bg-[#2a44a8]"
              >
                <Plus className="h-3.5 w-3.5" />
                Create Your First Prompt
              </button>
            </RequirePermission>
          </div>
        </div>

        <PromptFormDrawer
          initial={drawerInitial}
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          onSave={handleSave}
        />
        <Dialog />
      </RequirePermission>
    )
  }

  return (
    <RequirePermission permission="admin:extensions:view">
      <div className="space-y-4">
        {/* Header bar */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Prompt Catalog</h3>
            <p className="text-xs text-gray-500">
              {prompts.length} prompts ({counts.agent + counts.orchestrator + counts.routing + counts.summary + counts.safety} total). Create, version, and assign prompt templates to AI agents.
            </p>
          </div>
          <RequirePermission permission="admin:extensions:manage" fallback={null}>
            <button
              onClick={handleNewPrompt}
              className="flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#2a44a8]"
            >
              <Plus className="h-3.5 w-3.5" />
              Create Prompt
            </button>
          </RequirePermission>
        </div>

        {/* Search + filter bar */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Text search */}
          <div className="flex min-w-[200px] flex-1 items-center gap-2 rounded-sm border border-gray-200 bg-white px-3 py-2 focus-within:border-[#3454cf]">
            <Search className="h-3.5 w-3.5 shrink-0 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search prompts by name, slug, or description..."
              className="min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-gray-400"
            />
            {search && (
              <button onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-700">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {/* Category filter */}
                    <Select
            ariaLabel={"Category filter"}
            size="sm"
            value={categoryFilter}
            onChange={(v) => setCategoryFilter(v as 'all' | PromptTemplate['category'])}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c === 'all' ? 'All Categories' : CATEGORY_META[c].label} ({counts[c]})
              </option>
            ))}
                    </Select>
          {/* Clear filters button */}
          {(search || categoryFilter !== 'all') && (
            <button
              onClick={() => {
                setSearch('')
                setCategoryFilter('all')
              }}
              className="flex items-center gap-1 rounded-sm border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              <X className="h-3 w-3" />
              Clear Filters
            </button>
          )}
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className="flex items-center justify-between rounded-sm border border-[#3454cf]/20 bg-[#3454cf]/5 px-3 py-2">
            <div className="flex items-center gap-2 text-xs text-gray-700">
              <Check className="h-3.5 w-3.5 text-[#3454cf]" />
              <span className="font-medium">{selectedIds.size} selected</span>
            </div>
            <div className="flex items-center gap-2">
              <RequirePermission permission="admin:extensions:manage" fallback={null}>
                <button
                  type="button"
                  onClick={() => void handleBulkAssign()}
                  className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#2a44a8]"
                >
                  <Users className="h-3.5 w-3.5" /> Assign to Agents
                </button>
              </RequirePermission>
              <button
                type="button"
                onClick={clearSelection}
                className="rounded-sm border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Prompt table */}
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading prompts...
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-sm border border-gray-200 bg-white py-12 text-center shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
            <FileText className="mx-auto mb-2 h-8 w-8 text-gray-300" />
            <p className="text-sm font-medium text-gray-600">No prompts match your filters</p>
            <button
              onClick={() => { setSearch(''); setCategoryFilter('all') }}
              className="mt-2 text-xs font-medium text-[#3454cf] hover:underline"
            >
              Clear all filters
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-sm border border-gray-200 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
            <table className="w-full text-left text-xs">
              {/* Table header */}
              <thead className="border-b border-gray-200 bg-gray-50/50">
                <tr>
                  <th className="w-8 px-4 py-2.5">
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someOnPageSelected
                      }}
                      onChange={toggleSelectAllOnPage}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-[#3454cf] focus:ring-[#3454cf]"
                    />
                  </th>
                  <th className="px-4 py-2.5 font-semibold text-gray-500">
                    <button onClick={() => setSortBy('name')} className="flex items-center gap-1 hover:text-gray-700">
                      Name
                      {sortBy === 'name' && <ChevronDown className="h-3 w-3" />}
                    </button>
                  </th>
                  <th className="px-4 py-2.5 font-semibold text-gray-500">Description</th>
                  <th className="px-4 py-2.5 font-semibold text-gray-500">Category</th>
                  <th className="px-4 py-2.5 font-semibold text-gray-500">
                    <button onClick={() => setSortBy('version')} className="flex items-center gap-1 hover:text-gray-700">
                      Version
                      {sortBy === 'version' && <ChevronDown className="h-3 w-3" />}
                    </button>
                  </th>
                  <th className="px-4 py-2.5 font-semibold text-gray-500">Agents</th>
                  <th className="px-4 py-2.5 font-semibold text-gray-500">
                    <button onClick={() => setSortBy('updated')} className="flex items-center gap-1 hover:text-gray-700">
                      Updated
                      {sortBy === 'updated' && <ChevronDown className="h-3 w-3" />}
                    </button>
                  </th>
                  <th className="px-4 py-2.5 text-right font-semibold text-gray-500">Actions</th>
                </tr>
              </thead>
              {/* Table body */}
              <tbody className="divide-y divide-gray-50">
                {paginated.map((p) => {
                  const meta = CATEGORY_META[p.category]
                  const isExpanded = expandedPrompt === p.id
                  const isSelected = selectedIds.has(p.id)
                  const linkedCount = p.linkedAgents?.length ?? 0
                  const linkedAgents = p.linkedAgents ?? []

                  return (
                    <React.Fragment key={p.id}>
                      <tr className={`transition-colors ${isExpanded ? 'bg-gray-50/50' : 'hover:bg-gray-50/30'} ${isSelected ? 'bg-[#3454cf]/5' : ''}`}>
                        {/* Checkbox */}
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(p.id)}
                            className="h-3.5 w-3.5 rounded border-gray-300 text-[#3454cf] focus:ring-[#3454cf]"
                          />
                        </td>
                        {/* Name */}
                        <td className="px-4 py-3">
                          <button
                            className="flex items-center gap-1.5 text-left"
                            onClick={() => setExpandedPrompt(isExpanded ? null : p.id)}
                          >
                            <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-sm border ${meta.badge} ${meta.bg}`}>
                              {meta.icon}
                            </span>
                            <span className="font-semibold text-gray-900">{p.name}</span>
                          </button>
                        </td>
                        {/* Description */}
                        <td className="max-w-xs px-4 py-3">
                          <p className="truncate text-[11px] leading-relaxed text-gray-500" title={p.description}>
                            {p.description || 'No description'}
                          </p>
                        </td>
                        {/* Category */}
                        <td className="px-4 py-3">
                          <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-medium ${meta.badge}`}>
                            {meta.label}
                          </span>
                        </td>
                        {/* Version */}
                        <td className="px-4 py-3">
                          <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] font-medium text-gray-600">
                            v{p.currentVersion}
                          </span>
                        </td>
                        {/* Agents */}
                        <td className="px-4 py-3">
                          {linkedCount > 0 ? (
                            <div className="flex flex-wrap items-center gap-1" title={`Linked to: ${linkedAgents.map((a) => a.name).join(', ')}`}>
                              {linkedAgents.slice(0, 2).map((a) => (
                                <span key={a.id} className="flex items-center gap-0.5 rounded-sm bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-600">
                                  <Bot className="h-2.5 w-2.5" />
                                  {a.name}
                                </span>
                              ))}
                              {linkedCount > 2 && (
                                <span className="text-[9px] text-gray-400">+{linkedCount - 2} more</span>
                              )}
                            </div>
                          ) : (
                            <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-400">Unassigned</span>
                          )}
                        </td>
                        {/* Updated */}
                        <td className="px-4 py-3">
                          <span className="text-[10px] text-gray-400" title={p.updatedAt}>
                            {formatTimestamp(p.updatedAt)}
                          </span>
                        </td>
                        {/* Actions */}
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <RequirePermission permission="admin:extensions:manage" fallback={null}>
                              <button
                                onClick={() => handleManageAgents(p)}
                                className="rounded-sm p-1 text-gray-400 hover:bg-gray-100 hover:text-[#3454cf]"
                                title="Manage linked agents"
                              >
                                <Users className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => { setSelectedPrompt(p); handleEditPrompt() }}
                                className="rounded-sm p-1 text-gray-400 hover:bg-gray-100 hover:text-[#3454cf]"
                                title="Edit prompt"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            </RequirePermission>
                            <button
                              onClick={() => setExpandedPrompt(isExpanded ? null : p.id)}
                              className="rounded-sm p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                              title={isExpanded ? 'Collapse' : 'Expand details'}
                            >
                              {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {/* Expanded details row */}
                      {isExpanded && (
                        <tr className="bg-gray-50/50">
                          <td colSpan={8} className="px-4 py-3">
                            <div className="ml-5 space-y-3 border-l border-gray-200 pl-4">
                              {/* Slug */}
                              <div>
                                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Slug</div>
                                <p className="font-mono text-[11px] text-gray-600">{p.slug}</p>
                              </div>

                              {/* Content preview */}
                              <div>
                                <div className="mb-1 flex items-center gap-1">
                                  <AlignLeft className="h-3 w-3 text-gray-400" />
                                  <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Prompt Content</span>
                                </div>
                                <pre className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-sm border border-gray-100 bg-white p-2.5 font-mono text-[11px] leading-relaxed text-gray-600 thin-scrollbar">
                                  {p.content}
                                </pre>
                              </div>

                              {/* Variables */}
                              {p.variables.length > 0 && (
                                <div>
                                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Variables</div>
                                  <div className="flex flex-wrap gap-1">
                                    {p.variables.map((v) => (
                                      <span key={v} className="rounded-sm bg-blue-50 px-1.5 py-0.5 font-mono text-[10px] font-medium text-blue-700">
                                        {'{{' + v + '}}'}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Linked agents */}
                              <div>
                                <div className="mb-1 flex items-center gap-1">
                                  <Bot className="h-3 w-3 text-gray-400" />
                                  <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Linked Agents</span>
                                </div>
                                {linkedCount === 0 ? (
                                  <p className="text-[10px] text-gray-400">Not assigned to any agent yet.</p>
                                ) : (
                                  <div className="flex flex-wrap gap-1">
                                    {linkedAgents.map((a) => (
                                      <span key={a.id} className="rounded-sm bg-[#3454cf]/5 px-1.5 py-0.5 text-[10px] font-medium text-[#3454cf]">
                                        {a.name} <span className="text-[#3454cf]/60">({a.type})</span>
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Version history */}
                              {p.versions.length > 0 && (
                                <div>
                                  <div className="mb-1 flex items-center gap-1">
                                    <Clock className="h-3 w-3 text-gray-400" />
                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Version History ({p.versions.length})</span>
                                  </div>
                                  <div className={`space-y-1 ${p.versions.length > 5 ? 'max-h-48 overflow-y-auto thin-scrollbar' : ''}`}>
                                    {p.versions.map((v) => {
                                      const isCurrent = v.version === p.currentVersion
                                      return (
                                        <div key={v.version} className="flex items-center gap-2 rounded-sm border border-gray-100 bg-white px-2 py-1.5">
                                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[9px] font-bold text-gray-600">v{v.version}</span>
                                          <div className="min-w-0 flex-1">
                                            <p className="truncate text-[10px] text-gray-600">{v.notes || 'No notes'}</p>
                                            <p className="text-[9px] text-gray-400">{formatTimestamp(v.createdAt)}</p>
                                          </div>
                                          {isCurrent && (
                                            <span className="shrink-0 rounded-sm bg-emerald-50 px-1.5 py-0.5 text-[9px] font-medium text-emerald-600">Active</span>
                                          )}
                                          {!isCurrent && (
                                            <RequirePermission permission="admin:extensions:manage" fallback={null}>
                                              <button
                                                onClick={() => void handleRollback(v.version)}
                                                className="shrink-0 rounded-sm border border-gray-200 px-2 py-0.5 text-[9px] font-medium text-gray-600 hover:bg-gray-50"
                                              >
                                                Rollback
                                              </button>
                                            </RequirePermission>
                                          )}
                                        </div>
                                      )
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between rounded-sm border border-gray-200 bg-white px-4 py-2.5 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
            {/* Left: results info + page size */}
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-gray-500">
                Showing <span className="font-medium text-gray-700">{startIdx}-{endIdx}</span> of <span className="font-medium text-gray-700">{filtered.length}</span> prompt{filtered.length !== 1 ? 's' : ''}
              </span>
                            <Select
                ariaLabel={"Limit"}
                size="sm"
                value={String(limit)}
                onChange={(v) => { setLimit(Number(v)); setPage(1) }}
              >
                <option value="10">10 / page</option>
                <option value="25">25 / page</option>
                <option value="50">50 / page</option>
                            </Select>
            </div>

            {/* Right: page navigation */}
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(1)}
                  disabled={currentPage === 1}
                  className="rounded-sm border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                  title="First page"
                >
                  «
                </button>
                <button
                  onClick={() => setPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex items-center gap-0.5 rounded-sm border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                >
                  <ChevronLeft className="h-3 w-3" />
                  Prev
                </button>

                {/* Page number buttons */}
                {(() => {
                  const pages: number[] = []
                  const maxButtons = 5
                  let start = Math.max(1, currentPage - Math.floor(maxButtons / 2))
                  const end = Math.min(totalPages, start + maxButtons - 1)
                  start = Math.max(1, end - maxButtons + 1)
                  for (let i = start; i <= end; i++) pages.push(i)
                  return pages.map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`h-7 w-7 rounded-sm text-[11px] font-medium ${
                        p === currentPage
                          ? 'bg-[#3454cf] text-white'
                          : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  ))
                })()}

                <button
                  onClick={() => setPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-0.5 rounded-sm border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                >
                  Next
                  <ChevronRight className="h-3 w-3" />
                </button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="rounded-sm border border-gray-200 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                  title="Last page"
                >
                  »
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <PromptDetailDrawer
        prompt={selectedPrompt}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        onEdit={handleEditPrompt}
        onRollback={handleRollback}
      />

      <PromptFormDrawer
        initial={drawerInitial}
        linkedAgents={selectedPrompt?.linkedAgents ?? []}
        availableAgents={pickerAgents}
        assignedAgentIds={selectedPrompt?.linkedAgents?.map((a) => a.id) ?? []}
        attachedGuardrailIds={attachedGuardrailIds}
        otherPromptByAgentId={otherPromptForFormDrawer}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={handleSave}
      />

      <PromptAgentPickerDrawer
        promptName={agentPickerPrompt?.name ?? ''}
        agents={pickerAgents}
        assignedAgentIds={assignedAgentIds}
        otherPromptByAgentId={otherPromptByAgentId}
        isOpen={isAgentPickerOpen}
        onClose={() => {
          setIsAgentPickerOpen(false)
          setAgentPickerPrompt(null)
        }}
        onSave={handleSaveAgentAssignment}
      />

      <Dialog />
    </RequirePermission>
  )
}
